export const DRAWING_RELAY_COLORS = ['ink', 'coral', 'sun', 'aqua', 'eraser'] as const;
export const DRAWING_RELAY_WIDTHS = ['thin', 'thick'] as const;
export const DRAWING_RELAY_MAX_STROKES = 120;
export const DRAWING_RELAY_MAX_POINTS_PER_STROKE = 96;

export type DrawingRelayColor = (typeof DRAWING_RELAY_COLORS)[number];
export type DrawingRelayWidth = (typeof DRAWING_RELAY_WIDTHS)[number];
export type DrawingRelayTask = 'prompt' | 'draw' | 'guess';

export interface DrawingRelayPoint {
  readonly x: number;
  readonly y: number;
}

export interface DrawingRelayStroke {
  readonly id: string;
  readonly color: DrawingRelayColor;
  readonly width: DrawingRelayWidth;
  readonly points: readonly DrawingRelayPoint[];
}

export type DrawingRelayArtifact =
  | {
    readonly kind: 'text';
    readonly authorId: string;
    readonly stageIndex: number;
    readonly text: string;
    readonly timedOut: boolean;
  }
  | {
    readonly kind: 'drawing';
    readonly authorId: string;
    readonly stageIndex: number;
    readonly strokes: readonly DrawingRelayStroke[];
    readonly timedOut: boolean;
  };

export interface DrawingRelayStageView {
  readonly mode: 'stage';
  readonly matchId: string;
  readonly phaseId: number;
  readonly stageIndex: number;
  readonly stageCount: number;
  readonly task: DrawingRelayTask;
  readonly deadlineAt: number;
  readonly submittedPlayerIds: readonly string[];
  readonly previous: DrawingRelayArtifact | null;
  readonly draft: {
    readonly revision: number;
    readonly strokes: readonly DrawingRelayStroke[];
  } | null;
}

export interface DrawingRelayParticipantView {
  readonly id: string;
  readonly nickname: string;
  readonly colorIndex: number;
}

export interface DrawingRelayRevealView {
  readonly mode: 'reveal';
  readonly matchId: string;
  readonly phaseId: number;
  readonly chainIndex: number;
  readonly chainCount: number;
  readonly entries: readonly DrawingRelayArtifact[];
  readonly participants: readonly DrawingRelayParticipantView[];
}

export interface DrawingRelayCompleteView {
  readonly mode: 'complete';
  readonly matchId: string;
  readonly phaseId: number;
  readonly chains: readonly (readonly DrawingRelayArtifact[])[];
  readonly participants: readonly DrawingRelayParticipantView[];
}

export type DrawingRelayView =
  | DrawingRelayStageView
  | DrawingRelayRevealView
  | DrawingRelayCompleteView;

export function drawingRelayTask(stageIndex: number): DrawingRelayTask {
  if (stageIndex === 0) return 'prompt';
  return stageIndex % 2 === 1 ? 'draw' : 'guess';
}

export function drawingRelayActorForChain(
  chainIndex: number,
  stageIndex: number,
  playerCount: number,
): number {
  return (chainIndex + stageIndex) % playerCount;
}

export function drawingRelayChainForActor(
  actorIndex: number,
  stageIndex: number,
  playerCount: number,
): number {
  return (actorIndex - stageIndex + playerCount) % playerCount;
}

export function normalizeDrawingRelayText(value: string): string {
  return Array.from(value.trim().replace(/\s+/g, ' ')).slice(0, 40).join('');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function parseDrawingRelayStroke(value: unknown): DrawingRelayStroke | null {
  if (!isRecord(value)
    || typeof value.id !== 'string'
    || value.id.length < 1
    || value.id.length > 48
    || !DRAWING_RELAY_COLORS.some((color) => color === value.color)
    || !DRAWING_RELAY_WIDTHS.some((width) => width === value.width)
    || !Array.isArray(value.points)
    || value.points.length < 2
    || value.points.length > DRAWING_RELAY_MAX_POINTS_PER_STROKE) {
    return null;
  }
  const points: DrawingRelayPoint[] = [];
  for (const point of value.points) {
    if (!isRecord(point)
      || !Number.isSafeInteger(point.x)
      || !Number.isSafeInteger(point.y)
      || Number(point.x) < 0
      || Number(point.x) > 10_000
      || Number(point.y) < 0
      || Number(point.y) > 10_000) {
      return null;
    }
    points.push({ x: Number(point.x), y: Number(point.y) });
  }
  return {
    id: value.id,
    color: value.color as DrawingRelayColor,
    width: value.width as DrawingRelayWidth,
    points,
  };
}

function isDrawingRelayArtifact(value: unknown): value is DrawingRelayArtifact {
  if (!isRecord(value)
    || (value.kind !== 'text' && value.kind !== 'drawing')
    || typeof value.authorId !== 'string'
    || !Number.isSafeInteger(value.stageIndex)
    || typeof value.timedOut !== 'boolean') {
    return false;
  }
  if (value.kind === 'text') return typeof value.text === 'string' && Array.from(value.text).length <= 40;
  return Array.isArray(value.strokes)
    && value.strokes.length <= DRAWING_RELAY_MAX_STROKES
    && value.strokes.every((stroke) => parseDrawingRelayStroke(stroke) !== null);
}

function isDrawingRelayParticipant(value: unknown): value is DrawingRelayParticipantView {
  return isRecord(value)
    && typeof value.id === 'string'
    && typeof value.nickname === 'string'
    && Number.isSafeInteger(value.colorIndex);
}

export function isDrawingRelayView(value: unknown): value is DrawingRelayView {
  if (!isRecord(value) || typeof value.matchId !== 'string' || !Number.isSafeInteger(value.phaseId)) {
    return false;
  }
  if (value.mode === 'stage') {
    return Number.isSafeInteger(value.stageIndex)
      && Number.isSafeInteger(value.stageCount)
      && (value.task === 'prompt' || value.task === 'draw' || value.task === 'guess')
      && typeof value.deadlineAt === 'number'
      && Number.isFinite(value.deadlineAt)
      && Array.isArray(value.submittedPlayerIds)
      && value.submittedPlayerIds.every((playerId) => typeof playerId === 'string')
      && (value.previous === null || isDrawingRelayArtifact(value.previous))
      && (value.draft === null || (isRecord(value.draft)
        && Number.isSafeInteger(value.draft.revision)
        && Array.isArray(value.draft.strokes)
        && value.draft.strokes.every((stroke) => parseDrawingRelayStroke(stroke) !== null)));
  }
  if (value.mode === 'reveal') {
    return Number.isSafeInteger(value.chainIndex)
      && Number.isSafeInteger(value.chainCount)
      && Array.isArray(value.entries)
      && value.entries.every(isDrawingRelayArtifact)
      && Array.isArray(value.participants)
      && value.participants.every(isDrawingRelayParticipant);
  }
  return value.mode === 'complete'
    && Array.isArray(value.chains)
    && value.chains.every((chain) => Array.isArray(chain) && chain.every(isDrawingRelayArtifact))
    && Array.isArray(value.participants)
    && value.participants.every(isDrawingRelayParticipant);
}
